/**
 * Error Boundary Component - Prinde erorile React și previne crash-ul aplicației
 * Folosit la nivel de aplicație pentru a gestiona erori neașteptate
 */

import React from 'react';
import { AlertCircle, RefreshCw, Home } from 'lucide-react';
import { logger } from '../../utils/logger';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error) {
    // Actualizează state-ul pentru a afișa UI-ul de fallback
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log-uiește eroarea pentru debugging
    logger.error('Error caught by ErrorBoundary:', {
      error: error.toString(),
      errorInfo: errorInfo.componentStack,
    });

    // Salvează detaliile erorii în state
    this.setState({
      error,
      errorInfo,
    });

    // Aici poți adăuga integrare cu servicii de error tracking (Sentry, LogRocket, etc.)
    // Example: Sentry.captureException(error, { extra: errorInfo });
  }

  handleReload = () => {
    // Resetează state-ul și reîncarcă pagina
    this.setState({ hasError: false, error: null, errorInfo: null });
    window.location.reload();
  };

  handleGoHome = () => {
    // Resetează state-ul și navighează la home
    this.setState({ hasError: false, error: null, errorInfo: null });
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-50 via-white to-orange-50 px-4">
          <div className="max-w-2xl w-full bg-white rounded-3xl shadow-2xl p-8 md:p-12 border-t-8 border-red-500">
            {/* Icon Header */}
            <div className="flex justify-center mb-6">
              <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center">
                <AlertCircle className="w-12 h-12 text-red-600" />
              </div>
            </div>

            {/* Title */}
            <h1 className="text-3xl md:text-4xl font-extrabold text-red-700 mb-4 text-center">
              Oops! Ceva nu a mers bine
            </h1>

            {/* Description */}
            <p className="text-gray-600 text-center mb-8 text-lg">
              Ne pare rău, dar a apărut o eroare neașteptată. Te rugăm să
              încerci din nou sau să revii la pagina principală.
            </p>

            {/* Error Details (doar în development) */}
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <div className="mb-8 p-4 bg-red-50 border border-red-200 rounded-lg overflow-auto max-h-64">
                <p className="font-semibold text-red-800 mb-2">
                  Detalii eroare (development only):
                </p>
                <pre className="text-sm text-red-700 whitespace-pre-wrap">
                  {this.state.error.toString()}
                </pre>
                {this.state.errorInfo && (
                  <details className="mt-4">
                    <summary className="cursor-pointer font-semibold text-red-800 mb-2">
                      Component Stack
                    </summary>
                    <pre className="text-xs text-red-600 whitespace-pre-wrap">
                      {this.state.errorInfo.componentStack}
                    </pre>
                  </details>
                )}
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={this.handleReload}
                className="flex items-center justify-center gap-2 bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600 text-white font-bold py-3 px-6 rounded-xl shadow-lg transition-all duration-300 transform hover:scale-105"
              >
                <RefreshCw className="w-5 h-5" />
                Reîncarcă pagina
              </button>

              <button
                onClick={this.handleGoHome}
                className="flex items-center justify-center gap-2 bg-white hover:bg-gray-50 text-red-600 font-bold py-3 px-6 rounded-xl shadow-lg border-2 border-red-500 transition-all duration-300 transform hover:scale-105"
              >
                <Home className="w-5 h-5" />
                Înapoi la Home
              </button>
            </div>

            {/* Support Info */}
            <div className="mt-8 pt-6 border-t border-gray-200 text-center">
              <p className="text-sm text-gray-500">
                Dacă problema persistă, te rugăm să ne contactezi la{' '}
                <a
                  href="/contact"
                  className="text-red-600 font-semibold hover:underline"
                >
                  pagina de contact
                </a>
              </p>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
